import {
	type NodeViewComponentProps,
	useEditorEventCallback,
	useIgnoreMutation,
	useSelectNode,
	useStopEvent,
} from "@handlewithcare/react-prosemirror";
import cx from "classnames";
import { NodeSelection } from "prosemirror-state";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { ValueError } from "@moment/design-system";
import { camelCaseToDash } from "@moment/prosemirror-utils";
import { useDocumentFromCurrentRoute, usePageFromCurrentRoute } from "@moment/state";

import { ComputedCell } from "~/components/canvas/editor/cells/ComputedCell";
import { useComputeCells } from "~/components/canvas/hooks/useComputeCells";
import { Row } from "~/components/canvas/layout/row/Row";
import { getTemplateRaw } from "~/components/common/dnd";
import { useSendToCodeEditorStore } from "~/state/machines/code-editor";

export const ComputeCell = forwardRef<HTMLDivElement | null, NodeViewComponentProps>(
	function ComputeCell({ nodeProps, ...props }, outerRef) {
		const [isSelected, setIsSelected] = useState(false);

		const { documentId } = useDocumentFromCurrentRoute();
		const { pageId, pageMetadata } = usePageFromCurrentRoute();

		const { node } = nodeProps;
		const { attrs } = node;
		const cellID = attrs["id"];

		const ref = useRef<HTMLDivElement | null>(null);

		const send = useSendToCodeEditorStore();

		useSelectNode(
			() => {
				setIsSelected(true);
				send({ type: "openTab", cellID, activePageID: pageMetadata?.id });
			},
			() => {
				setIsSelected(false);
			}
		);

		const handleClick = useEditorEventCallback((view) => {
			view.dispatch(
				view.state.tr.setSelection(NodeSelection.create(view.state.doc, nodeProps.getPos()))
			);

			send({ type: "openTab", cellID, activePageID: pageMetadata?.id });
		});

		useEffect(() => {
			if (!isSelected) {
				return;
			}
			send({ type: "openTab", cellID, activePageID: pageMetadata?.id });
		}, [cellID, pageMetadata?.id, isSelected, send]);

		const dataSetProps = useMemo(() => {
			return Object.fromEntries(
				Object.entries(attrs).map(([key, value]) => [`data-${camelCaseToDash(key)}`, value])
			);
		}, [attrs]);

		const { compiledCells: cells } = useComputeCells(documentId, pageId);
		const cellInfo = cells[cellID];

		useStopEvent((view, event) => {
			// ProseMirror allows all nodes whose schema is `selectable` to be dragged and dropped
			// somewhere. We prevent this because compute cells will occasionally have drag components
			// of their own (and also it's weird).
			if (event instanceof DragEvent) {
				if (event.type === "drop") {
					// We handle two kinds of drop events: Drag and drop from
					// the cell controls and template drops. Let both of these
					// pass through to prosemirror.
					if (event.dataTransfer?.getData("moment-drag-cell-id")) {
						return false;
					}
					const templateRaw = getTemplateRaw(event);
					if (templateRaw !== undefined) {
						return false;
					}
				}
				event.preventDefault();
				return true;
			}

			if (event instanceof ClipboardEvent) {
				return document.getSelection()?.anchorNode !== view.dom;
			}

			return true;
		});

		useIgnoreMutation((view, mutation) => {
			return mutation.type === "selection";
		});

		return (
			<div
				// @ts-ignore -- We know that it will be overwritten, thank you TypeScript
				ref={(el) => {
					ref.current = el;
					if (!outerRef) {
						return;
					}
					if (typeof outerRef === "function") {
						outerRef(el);
					} else {
						outerRef.current = el;
					}
				}}
				id={cellID}
				data-node-type="compute_cell"
				className={cx(
					"group group/compute-cell relative compute-cell block focus-within:selection:bg-brand selection:bg-transparent min-h-8 focus:outline-none overflow-x-auto -my-1 py-1 pr-1 scroll-mt-10 scroll-mb-10",
					// IMPORTANT: `overflow-x-hidden` is REQUIRED to prevent the document being
					// extra wide when a compute cell "overflows" to the right, like the data grid.
					"overflow-x-hidden",
					{ "prosemirror-compute-cell--selected": isSelected }
				)}
				{...props}
				{...dataSetProps}
				onFocus={handleClick}
				onClick={handleClick}
				contentEditable={false}
			>
				<Row cellID={cellID} width={attrs["width"]}>
					<ErrorBoundary
						FallbackComponent={({ error }: { error: Error }) => {
							return <ValueError message={error.message} originalError={error} />;
						}}
					>
						{cellInfo && (
							<ComputedCell
								cellInfo={cellInfo}
								documentId={documentId}
								pageId={pageId}
							/>
						)}
					</ErrorBoundary>
				</Row>
			</div>
		);
	}
);
